import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Zap, CalendarDays, Infinity, AlertTriangle, Loader2, Banknote, CheckCircle2, FileText, CreditCard } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import SubscriptionTaxPreview from './subscriptions/SubscriptionTaxPreview';
import { SubscriptionTaxRate } from '../lib/subscriptionTax';
import toast from 'react-hot-toast';

interface SubscriptionRecord {
  id: string;
  business_id?: string;
  customer_id?: string | null;
  name: string;
  description: string | null;
  amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  billing_start: string;
  custom_start_date: string | null;
  duration: string;
  custom_end_date: string | null;
  require_signature: boolean;
  status: string;
  product_type: string | null;
  billing_mode?: string | null;
  manual_payment_method?: string | null;
  collection_mode?: string | null;
  subscription_number?: string | null;
  next_bill_date?: string | null;
  cancel_at_period_end?: boolean;
  failed_attempts?: number;
  stripe_subscription_id: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  activated_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  renewal_message_enabled: boolean;
  renewal_message_type: string | null;
  renewal_message_content: string | null;
}

interface SubscriptionInvoiceRow {
  invoice_id: string;
  invoice_number: string | null;
  status: string;
  total_cents: number;
  issued_at: string | null;
  description: string;
}

interface EditSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: SubscriptionRecord | null;
  onUpdate?: () => void;
}

type FrequencyOption = 'daily' | 'weekly' | 'monthly' | 'every_3_months' | 'every_6_months' | 'custom';

function toFrequencyOption(interval: string, count: number): { frequency: FrequencyOption; customUnit: 'day' | 'week' | 'month' | 'year'; customCount: string } {
  if (interval === 'day' && count === 1) return { frequency: 'daily', customUnit: 'day', customCount: '1' };
  if (interval === 'week' && count === 1) return { frequency: 'weekly', customUnit: 'week', customCount: '1' };
  if (interval === 'month' && count === 1) return { frequency: 'monthly', customUnit: 'month', customCount: '1' };
  if (interval === 'month' && count === 3) return { frequency: 'every_3_months', customUnit: 'month', customCount: '3' };
  if (interval === 'month' && count === 6) return { frequency: 'every_6_months', customUnit: 'month', customCount: '6' };
  return { frequency: 'custom', customUnit: interval as any, customCount: String(count) };
}

function getStripeInterval(frequency: string): { interval: string; interval_count: number } {
  switch (frequency) {
    case 'daily': return { interval: 'day', interval_count: 1 };
    case 'weekly': return { interval: 'week', interval_count: 1 };
    case 'monthly': return { interval: 'month', interval_count: 1 };
    case 'every_3_months': return { interval: 'month', interval_count: 3 };
    case 'every_6_months': return { interval: 'month', interval_count: 6 };
    default: return { interval: 'month', interval_count: 1 };
  }
}

export default function EditSubscriptionModal({ isOpen, onClose, subscription, onUpdate }: EditSubscriptionModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [taxRates, setTaxRates] = useState<SubscriptionTaxRate[]>([]);
  const [frequency, setFrequency] = useState<FrequencyOption>('monthly');
  const [customIntervalCount, setCustomIntervalCount] = useState('1');
  const [customIntervalUnit, setCustomIntervalUnit] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [billingStart, setBillingStart] = useState<'immediate' | 'custom'>('immediate');
  const [customStartDate, setCustomStartDate] = useState('');
  const [duration, setDuration] = useState<'forever' | 'custom'>('forever');
  const [customEndDate, setCustomEndDate] = useState('');
  const [requireSignature, setRequireSignature] = useState(true);
  const [productType, setProductType] = useState<'system_credit' | null>(null);
  const [renewalMessageEnabled, setRenewalMessageEnabled] = useState(false);
  const [renewalMessageType, setRenewalMessageType] = useState<'email' | 'sms' | 'both'>('email');
  const [renewalMessageContent, setRenewalMessageContent] = useState('');
  const renewalEditorRef = React.useRef<HTMLDivElement>(null);

  // Manual subscription billing cycles
  const [cycles, setCycles] = useState<any[]>([]);
  const [loadingCycles, setLoadingCycles] = useState(false);
  const [recordingCycleId, setRecordingCycleId] = useState<string | null>(null);

  // Engine (v2) subscriptions: real invoices per cycle.
  const [invoiceHistory, setInvoiceHistory] = useState<SubscriptionInvoiceRow[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activatingNow, setActivatingNow] = useState(false);
  const [togglingCancel, setTogglingCancel] = useState(false);
  const [collectionMode, setCollectionMode] = useState<'card' | 'invoice'>('card');
  const [savedCardLabel, setSavedCardLabel] = useState<string | null>(null);

  const isEngine = !!subscription?.collection_mode;
  const isManual = !isEngine && subscription?.billing_mode === 'manual';

  const loadInvoiceHistory = React.useCallback(async () => {
    if (!subscription?.id) return;
    setLoadingInvoices(true);
    try {
      const { data, error } = await supabase
        .from('invoice_line_items')
        .select('invoice_id, description, invoice:invoices!invoice_line_items_invoice_id_fkey(invoice_number, status, total_cents, finalized_at, created_at)')
        .eq('subscription_service_id', subscription.id)
        .order('created_at', { ascending: false })
        .limit(24);
      if (error) throw error;
      const seen = new Set<string>();
      const rows: SubscriptionInvoiceRow[] = [];
      for (const line of data || []) {
        if (seen.has(line.invoice_id)) continue;
        seen.add(line.invoice_id);
        const inv = (line as any).invoice;
        rows.push({
          invoice_id: line.invoice_id,
          invoice_number: inv?.invoice_number != null ? String(inv.invoice_number) : null,
          status: inv?.status || 'draft',
          total_cents: Number(inv?.total_cents) || 0,
          issued_at: inv?.finalized_at || inv?.created_at || null,
          description: line.description || '',
        });
      }
      setInvoiceHistory(rows);
    } catch (err) {
      console.error('Error loading subscription invoices:', err);
    } finally {
      setLoadingInvoices(false);
    }
  }, [subscription?.id]);

  useEffect(() => {
    if (isOpen && subscription?.id && isEngine) loadInvoiceHistory();
  }, [isOpen, subscription?.id, isEngine, loadInvoiceHistory]);

  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id) return;
    let cancelled = false;
    supabase
      .from('tax_rates')
      .select('name, rate')
      .eq('business_id', selectedBusiness.id)
      .then(({ data }) => {
        if (cancelled) return;
        setTaxRates(
          (data || [])
            .map((row) => ({ name: row.name as string, rate: Number(row.rate) || 0 }))
            .filter((row) => row.rate !== 0)
        );
      });
    return () => { cancelled = true; };
  }, [isOpen, selectedBusiness?.id]);

  const loadCycles = React.useCallback(async () => {
    if (!subscription?.id) return;
    setLoadingCycles(true);
    try {
      const { data, error } = await supabase
        .from('subscription_billing_cycles')
        .select('*')
        .eq('subscription_service_id', subscription.id)
        .order('period_start', { ascending: false });
      if (error) throw error;
      setCycles(data || []);
    } catch (err) {
      console.error('Error loading billing cycles:', err);
    } finally {
      setLoadingCycles(false);
    }
  }, [subscription?.id]);

  useEffect(() => {
    if (isOpen && subscription?.id && subscription.billing_mode === 'manual') {
      loadCycles();
    }
  }, [isOpen, subscription?.id, subscription?.billing_mode, loadCycles]);

  const handleRecordPayment = async (cycleId: string) => {
    setRecordingCycleId(cycleId);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('record-manual-subscription-payment', {
        body: { cycleId },
      });
      if (fnError || (data && data.error)) {
        throw new Error(data?.error || fnError?.message || 'Failed to record payment');
      }
      toast.success('Payment recorded');
      await loadCycles();
      onUpdate?.();
    } catch (err: any) {
      console.error('Error recording payment:', err);
      toast.error(err.message || 'Failed to record payment');
    } finally {
      setRecordingCycleId(null);
    }
  };

  const formatMethod = (method: string | null | undefined) => {
    switch (method) {
      case 'cash': return 'Cash';
      case 'cheque': return 'Cheque';
      case 'bank_transfer': return 'Bank transfer';
      case 'other': return 'Manual';
      default: return 'Manual';
    }
  };

  const formatCycleDate = (value: string) =>
    new Date(value + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  const hasOverdue = cycles.some((c) => c.status === 'overdue');

  useEffect(() => {
    if (isOpen && subscription) {
      setIsAnimating(true);
      setShowCancelConfirm(false);
      setCycles([]);
      setName(subscription.name);
      setDescription(subscription.description || '');
      setAmount(String(subscription.amount));

      const mapped = toFrequencyOption(subscription.interval, subscription.interval_count);
      setFrequency(mapped.frequency);
      setCustomIntervalCount(mapped.customCount);
      setCustomIntervalUnit(mapped.customUnit);

      setBillingStart(subscription.billing_start as 'immediate' | 'custom');
      setCustomStartDate(subscription.custom_start_date || '');
      setDuration(subscription.duration as 'forever' | 'custom');
      setCustomEndDate(subscription.custom_end_date || '');
      setRequireSignature(subscription.require_signature);
      setProductType(subscription.product_type || null);
      setRenewalMessageEnabled(subscription.renewal_message_enabled || false);
      setRenewalMessageType((subscription.renewal_message_type as 'email' | 'sms' | 'both') || 'email');
      setRenewalMessageContent(subscription.renewal_message_content || '');
      setCollectionMode(subscription.collection_mode === 'invoice' ? 'invoice' : 'card');
      setTimeout(() => {
        if (renewalEditorRef.current) {
          renewalEditorRef.current.innerHTML = subscription.renewal_message_content || '';
        }
      }, 100);
    }
  }, [isOpen, subscription]);

  useEffect(() => {
    if (!isOpen || !subscription?.customer_id || !subscription.collection_mode) {
      setSavedCardLabel(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('customer_saved_cards')
        .select('card_brand, card_last4')
        .eq('customer_id', subscription.customer_id!)
        .maybeSingle();
      if (cancelled) return;
      if (data?.card_last4) {
        const brand = data.card_brand
          ? data.card_brand.charAt(0).toUpperCase() + data.card_brand.slice(1)
          : 'Card';
        setSavedCardLabel(`${brand} •••• ${data.card_last4}`);
      } else {
        setSavedCardLabel(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, subscription?.customer_id, subscription?.collection_mode]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 200);
  };

  const isPending = subscription?.status === 'pending';
  const isActive = subscription?.status === 'active';
  const isEditable = isPending;

  const isFormValid = name.trim() !== '' && amount !== '' && parseFloat(amount) > 0 &&
    (billingStart !== 'custom' || customStartDate !== '') &&
    (duration !== 'custom' || customEndDate !== '') &&
    (frequency !== 'custom' || (parseInt(customIntervalCount) >= 1));

  const handleSave = async () => {
    if (!subscription || !selectedBusiness?.id || !isFormValid) return;

    setSaving(true);
    try {
      const updateData: any = {
        name: name.trim(),
        description: description.trim() || null,
        renewal_message_enabled: renewalMessageEnabled,
        renewal_message_type: renewalMessageEnabled ? renewalMessageType : null,
        renewal_message_content: renewalMessageEnabled ? renewalMessageContent : null,
      };

      if (isEngine && subscription.status !== 'cancelled' && subscription.status !== 'expired') {
        updateData.collection_mode = collectionMode;
      }

      if (isPending) {
        let interval: string;
        let intervalCount: number;
        if (frequency === 'custom') {
          interval = customIntervalUnit;
          intervalCount = parseInt(customIntervalCount) || 1;
        } else {
          const mapped = getStripeInterval(frequency);
          interval = mapped.interval;
          intervalCount = mapped.interval_count;
        }

        Object.assign(updateData, {
          amount: parseFloat(amount),
          interval,
          interval_count: intervalCount,
          billing_start: billingStart,
          custom_start_date: billingStart === 'custom' ? customStartDate : null,
          duration,
          custom_end_date: duration === 'custom' ? customEndDate : null,
          require_signature: requireSignature,
          product_type: productType,
        });
      }

      const { error } = await supabase
        .from('subscription_services')
        .update(updateData)
        .eq('id', subscription.id);

      if (error) throw error;

      toast.success('Subscription updated');
      onUpdate?.();
      handleClose();
    } catch (err) {
      console.error('Error updating subscription:', err);
      toast.error('Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!subscription) return;

    setCancelling(true);
    try {
      if (isEngine) {
        // Engine subs: pending cancels immediately; active/past_due run to the
        // end of the paid period, then stop (no proration).
        const { error } = await supabase.rpc('cancel_subscription_service', {
          p_subscription_id: subscription.id,
          p_cancel: true,
        });
        if (error) throw new Error(error.message);
        toast.success(isPending ? 'Subscription cancelled' : 'Cancellation scheduled — ends at the period end');
      } else if (isActive && subscription.stripe_subscription_id) {
        const { data, error: fnError } = await supabase.functions.invoke('cancel-subscription', {
          body: { subscriptionServiceId: subscription.id },
        });

        if (fnError || data?.error) {
          throw new Error(data?.error || fnError?.message || 'Failed to cancel subscription in Stripe');
        }
        toast.success('Subscription cancelled');
      } else {
        const { error } = await supabase
          .from('subscription_services')
          .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
          .eq('id', subscription.id);

        if (error) throw error;
        toast.success('Subscription cancelled');
      }

      onUpdate?.();
      handleClose();
    } catch (err: any) {
      console.error('Error cancelling subscription:', err);
      toast.error(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  // Revoke a scheduled cancellation (engine subs).
  const handleResumeBilling = async () => {
    if (!subscription) return;
    setTogglingCancel(true);
    try {
      const { error } = await supabase.rpc('cancel_subscription_service', {
        p_subscription_id: subscription.id,
        p_cancel: false,
      });
      if (error) throw new Error(error.message);
      toast.success('Cancellation revoked — billing continues');
      onUpdate?.();
      handleClose();
    } catch (err: any) {
      toast.error(err.message || 'Could not resume billing');
    } finally {
      setTogglingCancel(false);
    }
  };

  // Activate a pending engine subscription without the customer link (only
  // when no signature is needed; card mode needs a card on file — the server
  // enforces both).
  const handleActivateNow = async () => {
    if (!subscription || !selectedBusiness?.id) return;
    setActivatingNow(true);
    try {
      if (isEngine && collectionMode !== subscription.collection_mode) {
        const { error: modeError } = await supabase
          .from('subscription_services')
          .update({ collection_mode: collectionMode })
          .eq('id', subscription.id);
        if (modeError) throw modeError;
      }
      const { error } = await supabase.rpc('activate_subscription_service', {
        p_subscription_id: subscription.id,
        p_signature: null,
      });
      if (error) throw new Error(error.message);
      const { data: run } = await supabase.functions.invoke('subscription-billing', {
        body: { businessId: selectedBusiness.id, ...(subscription.customer_id ? { customerId: subscription.customer_id } : {}) },
      });
      const outcome = Array.isArray(run?.outcomes) ? run.outcomes[run.outcomes.length - 1]?.result : undefined;
      toast.success(
        outcome === 'charged' ? 'Subscription active — first payment charged to the card on file'
          : outcome === 'invoiced' ? 'Subscription active — first invoice issued'
          : outcome === 'charge_failed' ? 'Subscription active, but the first charge failed — a pay link was emailed'
          : 'Subscription activated'
      );
      onUpdate?.();
      handleClose();
    } catch (err: any) {
      toast.error(err.message || 'Could not activate subscription');
    } finally {
      setActivatingNow(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!subscription) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const isCancelledOrExpired = subscription.status === 'cancelled' || subscription.status === 'expired';

  return createPortal(
    <div
      className={`fixed inset-0 transition-opacity duration-200 overflow-x-hidden ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      } z-[100060]`}
      style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={handleClose}
    >
      <div
        className={`bg-white transform transition-all duration-200 max-w-full ${
          isMobile
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden min-w-0 max-w-full">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              {subscription.subscription_number ? `Subscription ${subscription.subscription_number}` : 'Edit Subscription'}
            </h2>
            <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
              <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                subscription.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : subscription.status === 'past_due'
                  ? 'bg-orange-100 text-orange-800'
                  : subscription.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : subscription.status === 'cancelled'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {subscription.status === 'past_due' ? 'Past due' : subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </span>
              {isEngine && (
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {collectionMode === 'card' ? 'Auto-charge' : 'Invoice-only'}
                </span>
              )}
              {isManual && (
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  Manual
                </span>
              )}
              {hasOverdue && (
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                  Overdue
                </span>
              )}
              {subscription.activated_at && (
                <span className="text-xs text-gray-500">
                  Activated {new Date(subscription.activated_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          {/* Form Body */}
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden bg-white min-w-0"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6 min-w-0 max-w-full`}>
              {/* Engine billing status */}
              {isEngine && (subscription.status === 'active' || subscription.status === 'past_due') && (
                <div className={`rounded-xl border p-4 ${
                  subscription.status === 'past_due'
                    ? 'border-orange-200 bg-orange-50'
                    : subscription.cancel_at_period_end
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  {subscription.status === 'past_due' ? (
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-orange-800">Payment past due</p>
                        <p className="text-xs text-orange-700 mt-1">
                          {Number(subscription.failed_attempts) > 0
                            ? `${subscription.failed_attempts} failed charge attempt${Number(subscription.failed_attempts) === 1 ? '' : 's'}. `
                            : ''}
                          The customer was emailed a pay link; retries run automatically. Billing pauses until the invoice is paid.
                        </p>
                      </div>
                    </div>
                  ) : subscription.cancel_at_period_end ? (
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-800">Cancellation scheduled</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Ends {subscription.next_bill_date ? new Date(subscription.next_bill_date + 'T00:00:00').toLocaleDateString() : 'at the period end'} — no further billing.
                        </p>
                        <button
                          type="button"
                          onClick={handleResumeBilling}
                          disabled={togglingCancel}
                          className="mt-2 px-3 py-1.5 bg-white border border-amber-300 text-amber-800 rounded-lg text-xs font-semibold hover:bg-amber-100 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                        >
                          {togglingCancel && <Loader2 className="h-3 w-3 animate-spin" />}
                          Resume billing
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        {collectionMode === 'card'
                          ? <CreditCard className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          : <FileText className="h-4 w-4 text-purple-600 flex-shrink-0" />}
                        <p className="text-sm text-gray-700">
                          {collectionMode === 'card'
                            ? 'Invoiced and charged to the card on file'
                            : 'Invoiced each period — you collect payment'}
                        </p>
                      </div>
                      {subscription.next_bill_date && (
                        <div className="text-right flex-shrink-0">
                          <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">Next bill</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {new Date(subscription.next_bill_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Engine pending: activate on the spot */}
              {isEngine && isPending && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2">
                  <p className="text-sm text-gray-700">
                    {subscription.require_signature
                      ? 'Waiting for the customer to sign and activate via the link.'
                      : collectionMode === 'card'
                        ? 'Waiting for a card — add one in the customer\u2019s profile and activate, or let the customer use the link.'
                        : 'Not started yet — activate now or let the customer use the link.'}
                  </p>
                  {!subscription.require_signature && (
                    <button
                      type="button"
                      onClick={handleActivateNow}
                      disabled={activatingNow}
                      className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {activatingNow ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                      {activatingNow ? 'Activating…' : 'Activate now'}
                    </button>
                  )}
                </div>
              )}

              {isEngine && !isCancelledOrExpired && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Collection</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setCollectionMode('card')}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        collectionMode === 'card'
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <CreditCard className="h-4 w-4 flex-shrink-0" />
                      Card on File
                    </button>
                    <button
                      type="button"
                      onClick={() => setCollectionMode('invoice')}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        collectionMode === 'invoice'
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <FileText className="h-4 w-4 flex-shrink-0" />
                      Invoice Only
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {collectionMode === 'card'
                      ? savedCardLabel
                        ? `${savedCardLabel} is on file. Each billing cycle generates an invoice and charges that card. This applies on the next bill.`
                        : 'No card on file yet. Add one on the customer before the next bill, or the charge fails and a pay link is emailed.'
                      : 'No card is charged. Each billing cycle generates an invoice on the customer’s payment terms. This applies on the next bill.'}
                  </p>
                </div>
              )}

              {/* Subscription Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isCancelledOrExpired}
                  placeholder="e.g. Monthly Maintenance Plan"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isCancelledOrExpired}
                  placeholder="Describe what this subscription includes..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {taxRates.length > 0 ? 'Amount before tax' : 'Amount'}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={!isEditable}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <SubscriptionTaxPreview amount={amount} rates={taxRates} />
                {!isEditable && !isCancelledOrExpired && (
                  <p className="text-xs text-gray-400 mt-1">Amount cannot be changed on an active subscription</p>
                )}
              </div>

              {/* Subscription Product */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Product</label>
                {isEditable ? (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setProductType(null)}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        productType === null
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      None
                    </button>
                    <button
                      type="button"
                      onClick={() => setProductType('system_credit')}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        productType === 'system_credit'
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      System Credit
                    </button>
                  </div>
                ) : (
                  <div className="px-4 py-3 border border-gray-300 rounded-xl text-sm bg-gray-50 text-gray-500">
                    {subscription?.product_type === 'system_credit' ? 'System Credit' : 'None'}
                  </div>
                )}
                {productType === 'system_credit' && (
                  <p className="text-xs text-gray-500 mt-2">
                    Each billing cycle will automatically add a credit to the customer's wallet equal to the billed amount.
                  </p>
                )}
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Frequency</label>
                {isEditable ? (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'every_3_months', label: 'Every 3 Months' },
                        { value: 'every_6_months', label: 'Every 6 Months' },
                        { value: 'custom', label: 'Custom' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFrequency(opt.value)}
                          className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-all text-center ${
                            frequency === opt.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {frequency === 'custom' && (
                      <div className="flex items-center gap-3 mt-3">
                        <span className="text-sm text-gray-600 font-medium flex-shrink-0">Every</span>
                        <input
                          type="number"
                          value={customIntervalCount}
                          onChange={(e) => setCustomIntervalCount(e.target.value)}
                          min="1"
                          max="156"
                          className="w-20 px-3 py-2.5 border border-gray-300 rounded-xl text-base text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                        />
                        <select
                          value={customIntervalUnit}
                          onChange={(e) => setCustomIntervalUnit(e.target.value as any)}
                          className="flex-1 px-3 py-2.5 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                          <option value="day">Days</option>
                          <option value="week">Weeks</option>
                          <option value="month">Months</option>
                          <option value="year">Years</option>
                        </select>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-4 py-3 border border-gray-300 rounded-xl text-sm bg-gray-50 text-gray-500">
                    {subscription.interval_count === 1
                      ? (subscription.interval === 'day' ? 'Daily' : subscription.interval === 'week' ? 'Weekly' : subscription.interval === 'month' ? 'Monthly' : 'Yearly')
                      : `Every ${subscription.interval_count} ${subscription.interval}s`}
                  </div>
                )}
                {!isEditable && !isCancelledOrExpired && (
                  <p className="text-xs text-gray-400 mt-1">Frequency cannot be changed on an active subscription</p>
                )}
              </div>

              {/* Bill Starting */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Bill Starting</label>
                {isEditable ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => { setBillingStart('immediate'); setCustomStartDate(''); }}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          billingStart === 'immediate'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Zap className="h-4 w-4 flex-shrink-0" />
                        Immediate
                      </button>
                      <button
                        type="button"
                        onClick={() => setBillingStart('custom')}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          billingStart === 'custom'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <CalendarDays className="h-4 w-4 flex-shrink-0" />
                        Custom Date
                      </button>
                    </div>
                    {billingStart === 'custom' && (
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full mt-3 px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    )}
                  </>
                ) : (
                  <div className="px-4 py-3 border border-gray-300 rounded-xl text-sm bg-gray-50 text-gray-500">
                    {subscription.billing_start === 'immediate' ? 'Immediate' : subscription.custom_start_date || 'Custom date'}
                  </div>
                )}
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Duration</label>
                {isEditable ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => { setDuration('forever'); setCustomEndDate(''); }}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          duration === 'forever'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Infinity className="h-4 w-4 flex-shrink-0" />
                        Forever
                      </button>
                      <button
                        type="button"
                        onClick={() => setDuration('custom')}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          duration === 'custom'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <CalendarDays className="h-4 w-4 flex-shrink-0" />
                        Custom Date
                      </button>
                    </div>
                    {duration === 'custom' && (
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        min={billingStart === 'custom' && customStartDate ? customStartDate : new Date().toISOString().split('T')[0]}
                        className="w-full mt-3 px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    )}
                  </>
                ) : (
                  <div className="px-4 py-3 border border-gray-300 rounded-xl text-sm bg-gray-50 text-gray-500">
                    {subscription.duration === 'forever' ? 'Until cancelled' : `Until ${subscription.custom_end_date || 'end date'}`}
                  </div>
                )}
              </div>

              {/* Require Signature */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Require Signature</label>
                {isEditable ? (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setRequireSignature(true)}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        requireSignature
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setRequireSignature(false)}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        !requireSignature
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <div className="px-4 py-3 border border-gray-300 rounded-xl text-sm bg-gray-50 text-gray-500">
                    {subscription.require_signature ? 'Yes' : 'No'}
                  </div>
                )}
              </div>

              {/* Subscription Renewal Message */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">Subscription Renewal Message</label>
                  <button
                    type="button"
                    onClick={() => setRenewalMessageEnabled(!renewalMessageEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      renewalMessageEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      renewalMessageEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {renewalMessageEnabled && (
                  <div className="space-y-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">Send via</label>
                      <div className="grid grid-cols-3 gap-2">
                        {(['email', 'sms', 'both'] as const).map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setRenewalMessageType(type)}
                            className={`px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
                              renewalMessageType === type
                                ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {type === 'email' ? 'Email' : type === 'sms' ? 'SMS' : 'Both'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">Message</label>
                      <div
                        ref={renewalEditorRef}
                        contentEditable
                        suppressContentEditableWarning
                        onInput={() => {
                          if (renewalEditorRef.current) {
                            setRenewalMessageContent(renewalEditorRef.current.innerHTML);
                          }
                        }}
                        className="w-full min-h-[120px] px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all prose prose-sm max-w-none"
                        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '16px' }}
                        data-placeholder="Enter renewal message for the customer..."
                      />
                      <style>{`
                        [data-placeholder]:empty:before {
                          content: attr(data-placeholder);
                          color: #9ca3af;
                          pointer-events: none;
                        }
                      `}</style>
                      <div className="flex items-center gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => document.execCommand('bold')}
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                          title="Bold"
                        >
                          <span className="text-xs font-bold">B</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => document.execCommand('italic')}
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                          title="Italic"
                        >
                          <span className="text-xs italic">I</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => document.execCommand('underline')}
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                          title="Underline"
                        >
                          <span className="text-xs underline">U</span>
                        </button>
                        <div className="w-px h-4 bg-gray-300 mx-1" />
                        <button
                          type="button"
                          onClick={() => document.execCommand('insertUnorderedList')}
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                          title="Bullet List"
                        >
                          <span className="text-xs">•&ensp;List</span>
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        This message will be sent to the customer each time the subscription renews.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Engine subscriptions: real invoices per cycle */}
              {isEngine && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <label className="text-sm font-semibold text-gray-700">Invoices</label>
                  </div>
                  {loadingInvoices ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                    </div>
                  ) : invoiceHistory.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4 text-center">
                      No invoices yet — each billing cycle generates one automatically.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {invoiceHistory.map((inv) => (
                        <div
                          key={inv.invoice_id}
                          className="flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl border border-gray-200 bg-white"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">
                                {inv.invoice_number ? `#${inv.invoice_number}` : 'Invoice'}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                inv.status === 'paid'
                                  ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20'
                                  : inv.status === 'void'
                                  ? 'bg-gray-50 text-gray-500 ring-1 ring-gray-500/20'
                                  : 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20'
                              }`}>
                                {inv.status.charAt(0).toUpperCase() + inv.status.slice(1).replace(/_/g, ' ')}
                              </span>
                            </div>
                            <div className="text-[12px] text-gray-500 mt-0.5 truncate">
                              {inv.description}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-sm font-semibold text-gray-900">${(inv.total_cents / 100).toFixed(2)}</div>
                            {inv.issued_at && (
                              <div className="text-[11px] text-gray-400">
                                {new Date(inv.issued_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Manual billing cycles / payment history */}
              {isManual && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Banknote className="h-4 w-4 text-gray-500" />
                      <label className="text-sm font-semibold text-gray-700">Payments</label>
                    </div>
                    <span className="text-xs text-gray-400">
                      Collected by {formatMethod(subscription.manual_payment_method).toLowerCase()}
                    </span>
                  </div>

                  {loadingCycles ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                    </div>
                  ) : cycles.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4 text-center">
                      {subscription.status === 'pending'
                        ? 'No billing cycles yet — they appear once the subscription is activated.'
                        : 'No billing cycles yet.'}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {cycles.map((cycle) => {
                        const isPaid = cycle.status === 'paid';
                        const isOverdue = cycle.status === 'overdue';
                        const isRecording = recordingCycleId === cycle.id;
                        return (
                          <div
                            key={cycle.id}
                            className="flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl border border-gray-200 bg-white"
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-900">
                                  ${Number(cycle.amount).toFixed(2)}
                                </span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  isPaid
                                    ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20'
                                    : isOverdue
                                    ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20'
                                    : cycle.status === 'skipped'
                                    ? 'bg-gray-50 text-gray-600 ring-1 ring-gray-500/20'
                                    : 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20'
                                }`}>
                                  {cycle.status.charAt(0).toUpperCase() + cycle.status.slice(1)}
                                </span>
                              </div>
                              <div className="text-[12px] text-gray-500 mt-0.5 truncate">
                                {isPaid && cycle.paid_at
                                  ? `Paid ${new Date(cycle.paid_at).toLocaleDateString()}`
                                  : `Due ${formatCycleDate(cycle.due_date)}`}
                              </div>
                            </div>
                            {isPaid ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                            ) : cycle.status === 'skipped' ? null : (
                              <button
                                type="button"
                                onClick={() => handleRecordPayment(cycle.id)}
                                disabled={isRecording}
                                className="flex-shrink-0 px-3 py-2 bg-blue-500 text-white rounded-lg text-xs font-semibold hover:bg-blue-600 transition-all active:scale-[0.98] flex items-center gap-1.5 disabled:opacity-50"
                              >
                                {isRecording ? (
                                  <>
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    Saving
                                  </>
                                ) : (
                                  'Record Payment'
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Cancel Subscription Section */}
              {!isCancelledOrExpired && (
                <div className="border-t border-gray-200 pt-6">
                  {!showCancelConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowCancelConfirm(true)}
                      className="w-full px-4 py-3 border border-red-300 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-50 transition-colors"
                    >
                      Cancel Subscription
                    </button>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-800">Are you sure?</p>
                          <p className="text-xs text-red-600 mt-1">
                            {isEngine && (isActive || subscription.status === 'past_due')
                              ? 'The subscription runs to the end of the current paid period, then stops — no further invoices or charges.'
                              : isActive
                              ? 'This will cancel the subscription in Stripe. The customer will no longer be billed.'
                              : 'This will cancel the pending subscription. The activation link will no longer work.'}
                          </p>
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => setShowCancelConfirm(false)}
                              disabled={cancelling}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-white transition-colors"
                            >
                              Keep It
                            </button>
                            <button
                              onClick={handleCancel}
                              disabled={cancelling}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors flex items-center gap-2"
                            >
                              {cancelling ? (
                                <>
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  Cancelling...
                                </>
                              ) : (
                                'Yes, Cancel'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          {!isCancelledOrExpired && (
            <div className="border-t border-gray-200 px-6 py-4 bg-white flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors active:scale-[0.98] touch-manipulation"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={!isFormValid || saving}
                className={`flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl text-sm font-semibold transition-all active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2 ${
                  !isFormValid || saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
                }`}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
